import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Breadcrumbs from '../components/Breadcrumbs';
import successSound from "../assets/sounds/valider.wav";
import failSound from "../assets/sounds/erreur.mp3";

const correctSound = new Audio(successSound);
const errorSound = new Audio(failSound);

const ExerciceVocabulaireCorrespondance = () => {

  const API_URL = process.env.REACT_APP_API_URL;
  const { interfaceLang, courseLang, vocabularyCategory } = useParams();  // Récupère l'ID de l'exercice à partir de l'URL
  const exerciseType = "matches"
  const navigate = useNavigate();
  const [sessionId, setSessionId] = useState(null);
  const [exercise, setExercise] = useState(null);
  const [shuffledLeft, setShuffledLeft] = useState([]);  // Colonnes mélangées à gauche
  const [shuffledRight, setShuffledRight] = useState([]); // Colonnes mélangées à droite
  const [feedback, setFeedback] = useState(null);

  const [leftSelected, setLeftSelected] = useState(null);  // Sélection à gauche
  const [rightSelected, setRightSelected] = useState(null); // Sélection à droite
  const [associations, setAssociations] = useState([]);
  const [errorAnimationLeft, setErrorAnimationLeft] = useState(null);
  const [errorAnimationRight, setErrorAnimationRight] = useState(null);
  const [successAnimationLeft, setSuccessAnimationLeft] = useState(null);
  const [successAnimationRight, setSuccessAnimationRight] = useState(null);
  const [exerciseCompleted, setExerciseCompleted] = useState(false);
  const [erroredKeys, setErroredKeys] = useState(() => new Set());

  // Contrôle du son
  const [isMuted, setIsMuted] = useState(false);

  // Fonction pour récupérer ou créer la session active pour l'exercice
  const fetchOrCreateSession = async () => {
    const token = localStorage.getItem('access_token');
    try {
      // Appel vers le backend pour récupérer la session active (ou en créer une nouvelle)
      const response = await axios.get(
        `${API_URL}/cours/api/vocabulaire/${vocabularyCategory}/session/${exerciseType}/`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setSessionId(response.data.id);
    } catch (error) {
      console.error("Erreur lors de la récupération de la session", error);
    }
  };

  // Lors du chargement du composant, on s'assure d'avoir une session valide
  useEffect(() => {
    fetchOrCreateSession();
  }, [vocabularyCategory, exerciseType]);


  useEffect(() => {
    if (!sessionId) return;
    const token = localStorage.getItem('access_token');
    // Réinitialiser les états
    setExercise(null);
    setFeedback(null);
    setAssociations([]); // Réinitialiser les associations
    setLeftSelected(null);
    setRightSelected(null);
    setExerciseCompleted(false); // Réinitialiser l'état de l'exercice terminé

    // Récupérer les 8 premiers mots à afficher pour les correspondances
    axios.get(`${API_URL}/cours/api/vocabulaire/${vocabularyCategory}/matches/?sessionId=${sessionId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    .then(response => {

      // response est une liste de 8 mots pas validé issue de la base avec word et traduction dans un format [ { "apple": "manzana" }, { "banana": "plátano" }, { "grape": "uva" } ....]
      setExercise(response.data);
      
      // On les mélange pour les afficher dans un ordre aléatoire
      const shuffled = shuffleArray(response.data);
      setShuffledLeft([...shuffled]);
      setShuffledRight([...shuffleArray(shuffled)]); 
    })
    .catch(error => {
      console.error("Erreur lors de la récupération des mots", error);
    });
  }, [sessionId, vocabularyCategory]);

  // Fonction pour mélanger un tableau de manière aléatoire
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Nouveaux helpers (au-dessus des handlers):
  const isLeftDone = (index) => {
    const key = Object.keys(shuffledLeft[index] || {})[0];
    return associations.some(pair => Object.keys(pair)[0] === key);
  };

  const isRightDone = (index) => {
    const val = Object.values(shuffledRight[index] || {})[0];
    return associations.some(pair => Object.values(pair)[0] === val);
  };

  const getKey = (obj) => Object.keys(obj || {})[0];
  const getVal = (obj) => Object.values(obj || {})[0];

  const isLeftDoneByKey = (key) =>
    associations.some(p => getKey(p) === key);

  const isRightDoneByPair = (key, val) =>
    associations.some(p => getKey(p) === key && getVal(p) === val);

  // Fonction pour gérer la sélection d'un bouton à gauche (choix d'entrée)
  const handleLeftClick = (index) => {
    const k = getKey(shuffledLeft[index]);
    if (isLeftDoneByKey(k)) return;
    //if (isLeftDone(index)) return; 
    // Si déjà sélectionné, on désélectionne
    if (leftSelected === index) {
      setLeftSelected(null);
    } else {
      setLeftSelected(index);  // Sinon, on sélectionne
      setFeedback('');  // Réinitialiser le message d'erreur

      if (rightSelected !== null) {
        checkAssociation(index, rightSelected);  // Vérifier l'association
      }
    }
  };

  // Fonction pour gérer la sélection d'un bouton à droite (solutions)
  const handleRightClick = (index) => {
    const k = getKey(shuffledRight[index]);
    const v = getVal(shuffledRight[index]);
    if (isRightDoneByPair(k, v)) return; //if (isRightDone(index)) return;
    // Si déjà sélectionné, on désélectionne
    if (rightSelected === index) {
      setRightSelected(null);
    } else {
      setRightSelected(index);  // Sinon, on sélectionne
      setFeedback('');  // Réinitialiser le message d'erreur
    }

    // Si un bouton à gauche et un bouton à droite sont sélectionnés, on vérifie l'association
    if (leftSelected !== null) {
      checkAssociation(leftSelected, index);  // Vérifier l'association
    }
  };

  // Fonction pour vérifier si l'association est correcte
  const checkAssociation = (leftIndex, rightIndex) => {
    const token = localStorage.getItem('access_token');
    const leftPair = shuffledLeft[leftIndex];  // Valeur à gauche mélangée
    const rightPair = shuffledRight[rightIndex];  // Valeur à droite mélangée
    
    const leftKey = Object.keys(leftPair)[0];
    const leftValue = Object.values(leftPair)[0];
    const rightKey = Object.keys(rightPair)[0];
    const rightValue = Object.values(rightPair)[0];

    // empêche toute double validation si la paire exacte est déjà faite
    if (isRightDoneByPair(rightKey, rightValue) || isLeftDoneByKey(leftKey)) {
      resetSelections();
      return;
    }

    // Comparer les paires par clé et valeur
    if (leftKey === rightKey && leftValue === rightValue) {
      
      if (!isMuted) correctSound.play();

      // Déclencher l'animation fireworks sur les deux boutons
      setSuccessAnimationLeft(leftIndex);
      setSuccessAnimationRight(rightIndex);

      setTimeout(() => {
        setSuccessAnimationLeft(null);
        setSuccessAnimationRight(null);
      }, 500);

      const wasErroredThisRound = erroredKeys.has(leftKey) || erroredKeys.has(rightKey);

      if (!wasErroredThisRound) {
        // Communiquer au backend que le mot est validé
        axios.post(`${API_URL}/cours/api/vocabulaire/${vocabularyCategory}/exercises/validate/`, {
          sessionId: sessionId,
          word: leftKey
        }, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        .then(response => {
          // Optionnel : on peut afficher un message de validation ponctuel ici
        })
        .catch(error => {
          console.error("Erreur lors de la validation du mot", error);
        });
      }
      
      // Utiliser une fonction de mise à jour pour s'assurer que les associations sont correctes
      setAssociations((prevAssociations) => {
        const newAssociations = [...prevAssociations, { [leftKey]: leftValue }];
        
        console.log(newAssociations)
        // Vérifier si toutes les associations sont faites
        if (newAssociations.length === exercise.length) {
          
          // On va alors chercher 8 nouveaux mots TODO
          handleExerciseCompletion(newAssociations);  // Valider l'exercice si terminé
        }
  
        return newAssociations;
      });
      
      setLeftSelected(null);
      setRightSelected(null);
      
    } else {

      if (!isMuted) errorSound.play();
      setFeedback('Erreur ! Réessayez.');  // Message d'erreur en cas de mauvaise association

      // Déclencher l'animation sur les deux boutons concernés
      setErrorAnimationLeft(leftIndex);
      setErrorAnimationRight(rightIndex);

      // Supprimer l'animation après 500ms
      setTimeout(() => {
        setErrorAnimationLeft(null);
        setErrorAnimationRight(null);
      }, 500);

      setErroredKeys(prev => {
        const next = new Set(prev);
        next.add(leftKey);   // mot choisi à gauche
        next.add(rightKey);  // mot qui correspond à la traduction cliquée
        return next;
      });

      console.log("Mauvais mots pour la clefs ",leftKey,"et",rightKey)
      // Communiquer au backend que l'association est incorrecte
      
      axios.post(`${API_URL}/cours/api/vocabulaire/${vocabularyCategory}/exercises/error/`, {
        sessionId: sessionId,
        word: leftKey
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      .then(response => {
        // On peut éventuellement gérer une réponse d'erreur ici
      })
      .catch(error => {
        console.error("Erreur lors de l'enregistrement de l'erreur", error);
      });

      axios.post(`${API_URL}/cours/api/vocabulaire/${vocabularyCategory}/exercises/error/`, {
        sessionId: sessionId,
        word: rightKey
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      .then(response => {
        // On peut éventuellement gérer une réponse d'erreur ici
      })
      .catch(error => {
        console.error("Erreur lors de l'enregistrement de l'erreur", error);
      });


      resetSelections();  // Réinitialiser uniquement les sélections
    }
  };
  

  // Réinitialiser uniquement les sélections (sans effacer les associations déjà faites)
  const resetSelections = () => {
    setLeftSelected(null);
    setRightSelected(null);
  };

  // Fonction pour marquer l'exercice comme terminé et récupérer 8 nouveaux mots
  const handleExerciseCompletion = (finalAssociations) => {
    const token = localStorage.getItem('access_token');
    // On peut informer le backend que l'exercice est terminé et envoyer les associations correctes
    //axios.post(`${API_URL}/cours/api/vocabulaire/${vocabularyCategory}/matches/complete/`, {
    //  sessionId: sessionId,
    //  associations: finalAssociations
    //}, {
    //  headers: {
    //    'Authorization': `Bearer ${token}`
    //  }
    //})
    //.then(response => {
      // Récupérer 8 nouveaux mots pour continuer l'exercice
    axios.get(`${API_URL}/cours/api/vocabulaire/${vocabularyCategory}/matches/?sessionId=${sessionId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    .then(response => {
      console.log(response.data);
      if (response.status === 204 || response.data == null || response.data.length == 0) {
          navigate(`/${interfaceLang}/courses/${courseLang}/vocabulary/${vocabularyCategory}`);
          return;
      }

      setErroredKeys(new Set()); // reset la mémoire des erreurs de cette série
      setExercise(response.data);
      const shuffled = shuffleArray(response.data);
      setShuffledLeft([...shuffled]);
      setShuffledRight([...shuffleArray(shuffled)]);
      setAssociations([]);
      setExerciseCompleted(false);
      setFeedback("Exercice validé !");
    })
    .catch(error => {
      console.error("Erreur lors de la récupération des nouveaux mots", error);
    });
    //})
    //.catch(error => {
    //  console.error("Erreur lors de la validation de l'exercice", error);
    //});
  };

  // fil d'Ariane
  const breadcrumbsLinks = [
    { label: 'Accueil', path: `/${interfaceLang}` },
    { label: 'Cours de langue', path:  `/${interfaceLang}/courses/${courseLang}` },
    { label: 'Vocabulaire', path: `/${interfaceLang}/courses/${courseLang}/vocabulary/${vocabularyCategory}` },
    { label: 'Exercice', path: `/${interfaceLang}/courses/${courseLang}/vocabulary/${vocabularyCategory}/exercise/${exerciseType}` }
  ];

  return (
    
    <div className="max-w-3xl mx-auto p-4 relative">
      {/* Fil d'Ariane */}
      <Breadcrumbs links={breadcrumbsLinks} />
      {/* Bouton pour activer/désactiver le son */}
      <button
        className="absolute top-10 md:top-4 right-4 p-2 rounded-full border border-gray-300 hover:bg-gray-100 transition"
        onClick={() => setIsMuted(prev => !prev)}
      >
        {isMuted ? "Activer le son" : "Couper le son"}
      </button>
      <h2 className="text-2xl font-bold text-center mb-4">Exercice de Correspondance</h2>
      <div className="flex justify-center gap-8">
        {/* Colonne gauche - Entrées */}
        <div className="flex flex-col gap-4 w-1/2">
          {shuffledLeft.map((option, index) => (
            <button
            key={index}
            className={`w-full h-12 rounded-lg text-lg font-medium border border-gray-300 transition-all duration-150 hover:shadow-md ${
              leftSelected === index 
              ? 'bg-yellow-300' 
              : isLeftDoneByKey(getKey(option)) 
              ? 'bg-green-200 text-green-500' 
              : 'bg-white can-hover'
            } ${errorAnimationLeft === index ? 'shake' : ''} ${successAnimationLeft === index ? 'fireworks' : ''}`}
            onClick={() => handleLeftClick(index)}
          >
            {Object.keys(option)[0]} {/* Clé (entrée) */}
          </button>
          ))}
        </div>

        {/* Colonne droite - Solutions */}
        <div className="flex flex-col gap-4 w-1/2">
          {shuffledRight.map((option, index) => (
            <button
            key={index}
            className={`w-full h-12 rounded-lg text-lg font-medium border border-gray-300 transition-all duration-150 hover:shadow-md ${
              rightSelected === index 
              ? 'bg-yellow-300' 
              : isRightDoneByPair(getKey(option), getVal(option)) 
              ? 'bg-green-200 text-green-500' 
              : 'bg-white can-hover'
            } ${errorAnimationRight === index ? 'shake' : ''} ${successAnimationRight === index ? 'fireworks' : ''}`}
            onClick={() => handleRightClick(index)}
          >
            {Object.values(option)[0]} {/* Valeur (solution) */}
          </button>
          ))}
        </div>
      </div>
      {feedback && <p className="text-center text-lg mb-2">{feedback}</p>}
      {/* Insertion du style pour l'animation shake */}
      <style jsx>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20%, 60% { transform: translateX(-10px); }
          40%, 80% { transform: translateX(10px); }
        }
        .shake {
          animation: shake 0.5s;
          background-color: #ff7c7c;
          color: #ff0000;
        }
        @keyframes fireworks {
          0% { transform: scale(0.5); opacity: 1; }
          100% { transform: scale(1.5); opacity: 0; }
        }
        .fireworks {
          animation: fireworks 0.5s;
        }

        @media (hover: hover) and (pointer: fine) {
          .can-hover:hover {
            box-shadow: var(--tw-shadow);
            --tw-shadow: 0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1);
            background-color: #eff6ff; /* bleu-50 */
          }
        }
      `}</style>
    </div>
  );
};

export default ExerciceVocabulaireCorrespondance;
